import { useEffect, useRef } from "react";
import type * as api from "../api/client";
import { formatGold, formatVoidJuice } from "../logic/currency";
import { playSfx } from "../logic/sfx";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { primaryBtn } from "../theme/styles";
import { CurrencyIcon } from "./CurrencyIcon";
import { ItemSprite } from "./ItemSprite";
import { useModalFocus } from "../logic/useModalFocus";

// Modal shown after a dialogue-option `grant:` actually pays out
// (the gratitude branch in Pip's questline is the canonical caller).
// Items render first with sprite + count + name; currencies follow
// with the standard CurrencyIcon. Plays the battle-victory harp on
// open so the reward beat has the same feel as winning a duel.

interface Props {
  entries: api.NpcGrantedEntry[];
  /** Extra highlighted lines shown below the item/currency rows —
   *  used for non-payout outcomes like "Elf faction unlocked!" when a
   *  bundle item is opened. Defaults to none. */
  notes?: string[];
  /** Heading shown at the top of the modal. Defaults to "REWARDS". */
  title?: string;
  onClose: () => void;
}

export function RewardsModal({ entries, notes = [], title = "REWARDS", onClose }: Props) {
  const theme = pixelTheme;
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  useEffect(() => {
    playSfx("ai_happy_harp_strum");
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Rewards"
      tabIndex={-1}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26, 15, 5, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          ...simplePanelStyle(theme),
          padding: 24,
          minWidth: 360,
          maxWidth: 480,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          {title}
        </div>

        {/* Cap the list height (a long bundle can carry 6+ rows) so
            the header and CLAIM button stay on-screen; the list itself
            scrolls. Fixed px — viewport units are disallowed here. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            maxHeight: 380,
            overflowY: "auto",
          }}
        >
          {entries.map((entry, i) => (
            <RewardRow key={i} entry={entry} />
          ))}
          {notes.map((note, i) => (
            <div
              key={`note-${i}`}
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 12,
                letterSpacing: theme.letterSpacing,
                color: theme.accent,
                textAlign: "center",
                padding: "10px 12px",
                border: `2px solid ${theme.panelEdge}`,
                background: theme.panel,
              }}
            >
              {note}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} {...primaryBtn(theme)}>
            CLAIM
          </button>
        </div>
      </div>
    </div>
  );
}

function RewardRow({ entry }: { entry: api.NpcGrantedEntry }) {
  const theme = pixelTheme;

  if (entry.kind === "item") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: 8,
          border: `2px solid ${theme.panelEdge}`,
          background: theme.panel,
        }}
      >
        <ItemSprite id={entry.item_id} size={64} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              color: theme.accent,
              letterSpacing: theme.letterSpacing,
            }}
          >
            ×{entry.count}
          </div>
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: 16,
              color: theme.ink,
            }}
          >
            {entry.name}
          </div>
        </div>
      </div>
    );
  }

  // Currency row — match the AppHeader chip styling so the value
  // reads as the same kind of resource. The droplet/coin PNGs are
  // light, which would vanish on the panel background — keep them
  // on the mulberry/amber chip the rest of the UI uses.
  const isJuice = entry.currency === "void_juice";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          background: isJuice ? E32.mulberry : E32.amber,
          border: `2px solid ${theme.panelEdgeStrong}`,
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <CurrencyIcon kind={isJuice ? "juice" : "gold"} size={18} />
      </div>
      <div
        style={{
          fontFamily: theme.fontBody,
          fontSize: 18,
          color: isJuice ? E32.mulberry : E32.oxblood,
        }}
      >
        {isJuice
          ? formatVoidJuice(entry.amount)
          : `${formatGold(entry.amount)} GOLD`}
      </div>
    </div>
  );
}
